import { useEffect, useState } from "react";
import { Loader2, Users, ChevronLeft, Trophy, Award, Flag } from "lucide-react";
import api from "@/lib/api";

export default function AdminGamePlayers() {
  const [players, setPlayers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    api.get("/admin/game/players").then((r) => setPlayers(r.data)).catch(() => setPlayers([]));
  }, []);

  const openPlayer = (p) => {
    setSelected(p);
    setDetail(null);
    setLoadingDetail(true);
    api.get(`/admin/game/players/${p.user_id}`).then((r) => setDetail(r.data)).catch(() => setDetail([])).finally(() => setLoadingDetail(false));
  };

  if (players === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>;

  if (selected) {
    return (
      <div className="space-y-5" data-testid="admin-game-player-detail">
        <button onClick={() => setSelected(null)} data-testid="back-to-players" className="flex items-center gap-1.5 text-sm font-semibold text-[#39FF14] hover:underline">
          <ChevronLeft size={16} /> Retour à la liste
        </button>
        <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
          <h2 className="text-xl font-black">{selected.name}</h2>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="rounded-lg bg-[#0F1812] p-3">
              <div className="text-[10px] uppercase tracking-wide text-[#697C6F]">Choix</div>
              <div className="tabular text-2xl font-black">{selected.picks ?? selected.plays}</div>
              {selected.pending > 0 && <div className="text-[10px] text-[#FFB020]">{selected.pending} en attente</div>}
            </div>
            <div className="rounded-lg bg-[#0F1812] p-3">
              <div className="text-[10px] uppercase tracking-wide text-[#697C6F] flex items-center gap-1"><Trophy size={11} className="text-[#FFD700]" /> % Gagnant</div>
              <div className="tabular text-2xl font-black text-[#FFD700]">{selected.plays > 0 ? `${selected.win_rate}%` : "—"}</div>
              <div className="text-[10px] text-[#697C6F]">{selected.wins}/{selected.plays}</div>
            </div>
            <div className="rounded-lg bg-[#0F1812] p-3">
              <div className="text-[10px] uppercase tracking-wide text-[#697C6F] flex items-center gap-1"><Award size={11} className="text-[#39FF14]" /> % Placé</div>
              <div className="tabular text-2xl font-black text-[#39FF14]">{selected.plays > 0 ? `${selected.place_rate}%` : "non classé"}</div>
              <div className="text-[10px] text-[#697C6F]">{selected.places}/{selected.plays}</div>
            </div>
          </div>
        </div>

        {loadingDetail ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#00E676]" /></div>
        ) : (
          <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] overflow-hidden" data-testid="player-picks">
            <div className="px-5 py-3 text-xs uppercase tracking-wide text-[#697C6F] border-b border-[#00E676]/10">Ses choix ({detail?.length || 0})</div>
            {(detail || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-[#697C6F]">Aucun choix.</div>
            ) : (
              <div className="divide-y divide-[#00E676]/5">
                {detail.map((h, i) => (
                  <div key={`${h.date}-${h.numero}-${i}`} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <div className="min-w-0">
                      <span className="text-[#697C6F]">{h.date ? new Date(h.date).toLocaleDateString("fr-FR") : "—"}</span>{" "}
                      <span className="font-medium">{h.hippodrome} {h.reunion} {h.course}</span>
                      <div className="text-xs text-[#A1B5A8] truncate">n°{h.numero} {h.cheval && `— ${h.cheval}`}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      {!h.settled ? (
                        <span className="text-xs text-[#697C6F]">en attente</span>
                      ) : h.won ? (
                        <span className="text-xs font-black px-2.5 py-1 rounded-full bg-[#FFD700] text-[#04180a]">Gagnant · {h.rank}er</span>
                      ) : h.placed ? (
                        <span className="text-xs font-black px-2.5 py-1 rounded-full bg-[#00E676]/20 text-[#39FF14]">Placé · {h.rank}e</span>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#1A2B20] text-[#697C6F]">{h.rank ? `${h.rank}e` : "Non placé"}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-game-players">
      <div>
        <h2 className="text-lg font-bold flex items-center gap-2"><Users size={18} className="text-[#00E676]" /> Choix des joueurs — Le Jeu</h2>
        <p className="text-sm text-[#697C6F] mt-1">% réussite : <b className="text-[#FFD700]">Gagnant</b> = cheval arrivé 1er · <b className="text-[#39FF14]">Placé</b> = top 3 (top 2 si moins de 8 partants). Visible par l'admin uniquement.</p>
      </div>
      {players.length === 0 ? (
        <p className="text-[#697C6F] text-sm py-8 text-center rounded-xl border border-[#00E676]/15 bg-[#131F17]">Aucun choix réglé pour le moment.</p>
      ) : (
        <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] overflow-x-auto">
          <table className="w-full text-sm" data-testid="game-players-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#697C6F] border-b border-[#00E676]/10">
                <th className="px-5 py-3">Joueur</th>
                <th className="px-5 py-3 text-center">Choix</th>
                <th className="px-5 py-3 text-center">% Gagnant</th>
                <th className="px-5 py-3 text-center">% Placé</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <tr key={p.user_id} className="border-b border-[#00E676]/5 hover:bg-[#1A2B20]/40 cursor-pointer" onClick={() => openPlayer(p)} data-testid={`game-player-${p.user_id}`}>
                  <td className="px-5 py-3 font-semibold">
                    <span className="hover:text-[#39FF14] hover:underline underline-offset-2">{p.name}</span>
                  </td>
                  <td className="px-5 py-3 text-center tabular text-[#A1B5A8]">
                    {p.picks ?? p.plays}
                    {p.pending > 0 && <span className="ml-1 text-[10px] text-[#FFB020]">({p.pending} en attente)</span>}
                  </td>
                  <td className="px-5 py-3 text-center tabular font-black text-[#FFD700]">
                    {p.plays > 0 ? <>{p.win_rate}% <span className="text-[10px] text-[#697C6F]">({p.wins})</span></> : <span className="text-[#697C6F] font-normal text-xs">—</span>}
                  </td>
                  <td className="px-5 py-3 text-center tabular font-black text-[#39FF14]">
                    {p.plays > 0 ? <>{p.place_rate}% <span className="text-[10px] text-[#697C6F]">({p.places})</span></> : <span className="text-[#697C6F] font-normal text-xs">non classé</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
